import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  Clock,
  User,
  RefreshCw,
  Filter
} from 'lucide-react';
import { getAdminConsultations, updateConsultationStatus } from '../api';
import { toast } from '../hooks/use-toast';

const AdminConsultations = () => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-yellow-100 text-yellow-800',
    scheduled: 'bg-purple-100 text-purple-800',
    converted: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800'
  };

  const typeLabels = {
    driven: 'Being DRIVEN',
    driving: 'DRIVING Services',
    both: 'Full Auto Lifestyle'
  };

  useEffect(() => {
    // Check auth
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) {
          navigate('/admin/login');
        }
      } catch {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const loadConsultations = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await getAdminConsultations(params);
      setConsultations(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load consultations', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConsultations();
  }, [statusFilter]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await updateConsultationStatus(leadId, newStatus);
      toast({ title: 'Updated', description: `Status changed to ${newStatus}` });
      loadConsultations();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin')}
              className="text-gray-300 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold">Consultation Leads</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{total} total</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadConsultations}
              className="border-gray-700 text-gray-300 hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <Card className="p-4 mb-6 border-gray-200">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filter by status:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Consultations List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : consultations.length === 0 ? (
          <Card className="p-12 text-center border-gray-200">
            <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No consultations found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {consultations.map((consultation) => (
              <Card key={consultation.leadId} className="p-6 border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-mono text-gray-500">{consultation.leadId}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[consultation.status]}`}>
                        {consultation.status}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {typeLabels[consultation.consultationType] || consultation.consultationType}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-black">
                      {consultation.firstName} {consultation.lastName}
                    </h3>
                    
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Mail className="h-4 w-4" />
                        {consultation.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {consultation.phone}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDate(consultation.createdAt)}
                      </span>
                    </div>

                    {consultation.currentSituation && (
                      <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                        {consultation.currentSituation}
                      </p>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {consultation.vehicleInterest && (
                        <span className="px-2 py-1 bg-gray-100 rounded">
                          Interest: {consultation.vehicleInterest}
                        </span>
                      )}
                      {consultation.timeframe && (
                        <span className="px-2 py-1 bg-gray-100 rounded">
                          Timeframe: {consultation.timeframe}
                        </span>
                      )}
                      {consultation.budget && (
                        <span className="px-2 py-1 bg-gray-100 rounded">
                          Budget: {consultation.budget}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Select 
                      value={consultation.status} 
                      onValueChange={(value) => handleStatusChange(consultation.leadId, value)}
                    >
                      <SelectTrigger className="w-36" data-testid={`status-select-${consultation.leadId}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConsultations;
